// A board's or a space's Files tab (TASK-FILES; Work OS's board attachments view): every live
// file on the place's tasks the caller can see (`files.list?board_id|space_id`, newest first),
// with a name filter, each row naming its task (a click opens the task's drawer) and the same
// preview, open and download icons as the drawer. wp/ACTIONS: rename and delete are hover icons
// here too (the drawer's dialogs); a delete asks first, moves the file to its task's trash and
// offers Restore in its toast. The trash itself is per task (`files.list?trashed` needs a
// task_id), so the full trash stays in the task's drawer. Covers stay in the drawer.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  EmptyState,
  SearchField,
  Skeleton,
  toast,
} from "@companyos/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PaperclipIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { type FileItem, FileRow, fileKeys, PreviewDialog, RenameDialog } from "./files";
import { workKeys } from "./work-queries";

export function PlaceFiles({
  api,
  company,
  kind,
  id,
  onOpenTask,
}: {
  api: Api;
  company: CompanyAccess;
  kind: "board" | "space";
  id: string;
  onOpenTask: (id: string) => void;
}) {
  const co = company.id;
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState<FileItem | null>(null);
  const [renaming, setRenaming] = useState<FileItem | null>(null);
  const [deleting, setDeleting] = useState<FileItem | null>(null);
  const qc = useQueryClient();
  const refresh = (f: FileItem) => {
    void qc.invalidateQueries({ queryKey: fileKeys.place(co, kind, id) });
    if (f.task) void qc.invalidateQueries({ queryKey: fileKeys.task(co, f.task.id) });
    if (f.task) void qc.invalidateQueries({ queryKey: workKeys.history(co, f.task.id) });
  };
  const rename = useMutation({
    mutationFn: (v: { file: FileItem; name: string }) =>
      api.client.files.update({ params: { co, id: v.file.id }, body: { file_name: v.name } }),
    onSettled: (_r, _e, v) => refresh(v.file),
  });
  const restore = useMutation({
    mutationFn: (f: FileItem) => api.client.files.restore({ params: { co, id: f.id } }),
    onSuccess: (_r, f) => toast.add({ title: `${f.file_name} restored`, type: "success" }),
    onSettled: (_r, _e, f) => refresh(f),
  });
  const remove = useMutation({
    mutationFn: (f: FileItem) => api.client.files.delete({ params: { co, id: f.id } }),
    onSuccess: (_r, f) =>
      toast.add({
        title: `${f.file_name} moved to the trash`,
        ...(f.task ? { description: `In the trash of “${f.task.title}”` } : {}),
        actionProps: { children: "Restore", onClick: () => restore.mutate(f) },
      }),
    onSettled: (_r, _e, f) => refresh(f),
  });
  const files = useQuery({
    queryKey: [...fileKeys.place(co, kind, id), q.trim()],
    queryFn: () =>
      api.client.files.list({
        params: { co },
        query: {
          ...(kind === "board" ? { board_id: id } : { space_id: id }),
          ...(q.trim() ? { q: q.trim() } : {}),
          limit: 200,
        },
      }),
    placeholderData: keepPreviousData,
  });
  const rows = files.data?.data ?? [];
  return (
    <section aria-label="Files" data-slot="place-files" className="mt-3 flex flex-col gap-2">
      <SearchField
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter files by name"
        aria-label="Filter files by name"
      />
      {files.isPending ? (
        <div role="status" aria-label="Loading the files" className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : rows.length ? (
        <ul aria-label="Files" className="m-0 flex max-w-3xl list-none flex-col p-0">
          {rows.map((f) => (
            <FileRow
              key={f.id}
              api={api}
              co={co}
              file={f}
              actions={{ onPreview: setPreview, onRename: setRenaming, onDelete: setDeleting }}
              context={
                f.task ? (
                  <button
                    type="button"
                    className="block max-w-full truncate text-left text-xs text-secondary hover:underline"
                    onClick={() => f.task && onOpenTask(f.task.id)}
                  >
                    {f.task.number ? `#${f.task.number} ` : ""}
                    {f.task.title}
                    {f.folder_path ? ` · ${f.folder_path}` : ""}
                  </button>
                ) : null
              }
            />
          ))}
        </ul>
      ) : (
        <EmptyState
          variant="compact"
          icon={PaperclipIcon}
          title={q.trim() ? "No file matches" : "No files yet"}
          description={
            q.trim()
              ? "Try another name."
              : "Files attached to this place's tasks show here. Attach one from a task's Files section."
          }
        />
      )}
      <PreviewDialog api={api} co={co} file={preview} onClose={() => setPreview(null)} />
      {renaming ? (
        <RenameDialog
          key={renaming.id}
          title="Rename file"
          initial={renaming.file_name}
          hint="A “/” moves it into that folder of its task (made if needed), e.g. “Final/brief.pdf”."
          onClose={() => setRenaming(null)}
          onSave={(name) => {
            rename.mutate({ file: renaming, name });
            setRenaming(null);
          }}
        />
      ) : null}
      <AlertDialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-slot="file-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.file_name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              It moves to its task's trash, where it can be restored until it is purged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleting) remove.mutate(deleting);
                setDeleting(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
